import React, { useEffect, useRef } from 'react'
import { InView } from 'react-intersection-observer'
import {
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { ResponsiveValues } from '@sketch/global-styles'
import { clamp } from '@sketch/utils'
import {
  Table,
  AspectRatioGrid,
  useBreakpoint,
  TableHeaderItem,
} from '@sketch/components'
import { TableWrapper } from './StorageItemsLayout.styles'

const COLUMNS: ResponsiveValues<number> = [2, 2, 3, 3, 3, 4, 5, 6]
const GUTTER: ResponsiveValues<number> = [16, 16, 16, 16, 24, 24, 24]
const VERTICAL_SPACE: ResponsiveValues<number> = [24, 24, 24, 24, 24, 24, 24]
const PAGE_SIZE = 20

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

export type RenderStorageItem<S> = (item: S) => React.ReactNode

export interface BaseStorageItemsLayoutProps<S> extends CustomColumns {
  items: S[]
  onLoadMore: () => Promise<unknown>
  totalCount?: number

  /**
   * Which columns display when showing the table layout
   */
  header?: TableHeaderItem[]
}

interface StorageItemsLayoutProps<S> extends BaseStorageItemsLayoutProps<S> {
  search?: string
  Skeleton: React.ElementType<{ inViewListener?: React.ReactNode }>
  renderItem: RenderStorageItem<S>
  extraPlaceholders?: number
}

export interface StorageItemRenderContentArgs {
  item: StorageItemFragment
  isDraggedOver: boolean
  isDraggedOverItself: boolean
}

export type CellRenderArgs =
  | { type: 'plain'; content: React.ReactNode }
  | { type: 'dropdown'; content?: React.ReactNode }

export type RenderCell = (args: StorageItemRenderContentArgs) => CellRenderArgs

const buildPlaceholderArray = (remainingItemsToLoad: number): undefined[] =>
  Array.from({ length: clamp(remainingItemsToLoad, 0, PAGE_SIZE) })

export const StorageItemsLayout = <S extends StorageItemFragment>(
  props: StorageItemsLayoutProps<S>
) => {
  const {
    items,
    onLoadMore,
    totalCount = 0,
    customColumns,
    header: tableColumns = [],
    Skeleton,
    renderItem,
    extraPlaceholders = 0,
  }: typeof props = props

  const isMobile = !useBreakpoint('sm')

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<unknown>>(onLoadMore)

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  const placeholders = buildPlaceholderArray(
    totalCount + Math.max(extraPlaceholders, 0) - items.length
  )

  // Merge the items with the placeholders have them rendered together
  // This prevents the page from bumping when the placeholders are replaced
  // with actual items
  const itemsAndPlaceholders = [...items, ...placeholders]

  // Create the InView listener warn when it starts showing the placeholders, this will load the next page
  const inViewListener =
    placeholders.length > 0 ? (
      <InView onChange={inView => inView && onLoadMoreRef.current?.()}>
        <span className="sr-only">Loading Placeholder</span>
      </InView>
    ) : null

  if (documentsLayout === 'LIST') {
    return (
      <TableWrapper>
        <Table
          header={tableColumns}
          items={itemsAndPlaceholders}
          renderItemKey={item =>
            // if it's not a share the table will generate a index based key
            typeof item === 'object' ? item.identifier : undefined
          }
          renderItem={item => {
            if (typeof item === 'object') {
              return renderItem(item)
            }

            return <Skeleton inViewListener={inViewListener} />
          }}
          evenColumns={!isMobile}
        />
      </TableWrapper>
    )
  }

  return (
    <AspectRatioGrid
      columns={customColumns ?? COLUMNS}
      verticalRowSpace={VERTICAL_SPACE}
      gutterSize={GUTTER}
    >
      {itemsAndPlaceholders.map((item, index) => {
        if (!item?.__typename)
          return (
            <Skeleton
              key={`skeleton-${index}`}
              inViewListener={inViewListener}
            />
          )

        return renderItem(item)
      })}
    </AspectRatioGrid>
  )
}
